import { useEffect, useState } from "react";
import { Box, Typography, Paper, CircularProgress, Button, Stack } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import HomeIcon from "@mui/icons-material/Home";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { shouldUseLegacyPaymentProcess, getProcessMessage, isContractFeatureAvailable, isSmartphone, isTablet, isWearable } from "../utils/deviceCategoryUtils";
import { Application } from "./admin/types/application";

export default function AcceptPage() {
  const { application_id } = useParams<{ application_id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "fail" | "alreadyAccepted">("loading");

  useEffect(() => {
    if (!application_id) return setStatus("fail");

    // 🔍 신청서 정보를 확인하여 기기별 카테고리 검증
    const checkApplicationAndProcess = async () => {
      try {
        const docRef = doc(db, 'applications', application_id);
        const docSnap = await getDoc(docRef);
        
        if (!docSnap.exists()) {
          setStatus("fail");
          return;
        }

        const appData = { id: docSnap.id, ...docSnap.data() } as Application;
        const params = new URLSearchParams(location.search);
        const device_id = params.get('device_id');

        // 🔍 device_id가 있는 경우 해당 기기의 카테고리 확인
        if (device_id) {
          console.log('🔍 [AcceptPage] device_id 발견:', device_id);
          console.log('🔍 [AcceptPage] 전체 기기 목록:', appData.devices);
          
          const targetDevice = appData.devices?.find(device => device.id === device_id);
          console.log('🔍 [AcceptPage] 대상 기기:', targetDevice);
          
          if (!targetDevice) {
            console.log('❌ [AcceptPage] 대상 기기를 찾을 수 없음');
            setStatus("fail");
            return;
          }

          console.log('🔍 [AcceptPage] 기기 타입:', targetDevice.deviceType);
          console.log('🔍 [AcceptPage] 기기 상태:', (targetDevice as any).status);
          console.log('🔍 [AcceptPage] 기기 계약 플래그:', !!(targetDevice as any).contract_signed_at);

          // ✅ 이미 완료된 기기인지 먼저 체크
          const decided = ['입금대기', '완료', '반송'];
          if (decided.includes((targetDevice as any).status) || !!(targetDevice as any).contract_signed_at) {
            console.log('✅ [AcceptPage] 기기가 이미 처리됨, alreadyAccepted로 안내');
            setStatus("alreadyAccepted");
            return;
          }

          // 🟢 웨어러블: 기존 개별 기기 입금 처리
          const isThisDeviceWearable = isWearable(targetDevice.deviceType);
          const isThisDeviceSmartphone = isSmartphone(targetDevice.deviceType);
          const isThisDeviceTablet = isTablet(targetDevice.deviceType);
          
          console.log('🔍 [AcceptPage] 기기 분류 결과:', {
            deviceType: targetDevice.deviceType,
            isWearable: isThisDeviceWearable,
            isSmartphone: isThisDeviceSmartphone,
            isTablet: isThisDeviceTablet
          });
          
          if (isThisDeviceWearable) {
            console.log('⌚ [AcceptPage] 웨어러블 감지 - 기존 입금 처리');
            fetch(`/api/applications/${application_id}/devices/${device_id}`, {
              method: "PATCH",
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ status: '입금대기' })
            })
              .then(async (res) => {
                if (res.ok) return setStatus("success");
                if (res.status === 409) {
                  setStatus("alreadyAccepted");
                } else {
                  setStatus("fail");
                }
              })
              .catch(() => setStatus("fail"));
            return;
          }

          // 📱📱 스마트폰/태블릿: 계약서 페이지로 이동 (웨어러블이 아닌 경우에만)
          if (isThisDeviceSmartphone || isThisDeviceTablet) {
            console.log('📱 [AcceptPage] 스마트폰/태블릿 감지 - 계약서 페이지로 리다이렉트');
            navigate(`/contract/${application_id}?device_id=${device_id}`, { replace: true });
            return;
          }

          // 🚫 분류되지 않은 기기 타입: 안전하게 실패 처리
          console.warn('❌ [AcceptPage] 알 수 없는 기기 타입:', targetDevice.deviceType);
          setStatus("fail");
          return;
        }

          // 🔍 device_id가 없는 경우: 스마트폰/태블릿이 하나라도 있으면 계약서, 그 외(웨어러블만)면 기존 처리
        if (isContractFeatureAvailable(appData.devices || [])) {
          console.log('🔍 [AcceptPage] 스마트폰/태블릿 포함 신청서, 전체 계약 확인');
          // 모든 스마트폰/태블릿 기기가 이미 완료/입금대기/반송이면 재계약 방지
          const decided = ['입금대기', '완료', '반송'];
          const anyOpen = (appData.devices || []).some((d: any) => {
            const isEligible = isSmartphone(d.deviceType) || isTablet(d.deviceType);
            const isOpen = !decided.includes(d.status) && !(d as any).contract_signed_at;
            console.log(`🔍 [AcceptPage] 기기 ${d.id}: ${d.deviceType}, 대상=${isEligible}, 미완료=${isOpen}, 상태=${d.status}`);
            return isEligible && isOpen;
          });
          console.log('🔍 [AcceptPage] 미완료 기기 존재 여부:', anyOpen);
          if (!anyOpen) {
            console.log('✅ [AcceptPage] 모든 대상 기기가 완료됨, alreadyAccepted로 안내');
            setStatus("alreadyAccepted");
            return;
          }
          console.log('📱 [AcceptPage] 전체 계약서 페이지로 리다이렉트');
          navigate(`/contract/${application_id}`, { replace: true });
          return;
        }

        // 웨어러블만 있는 경우 전체 기존 처리
        fetch(`/api/accept/${application_id}`, { method: "POST" })
          .then(async (res) => {
            if (res.ok) return setStatus("success");
            if (res.status === 409) {
              setStatus("alreadyAccepted");
            } else {
              setStatus("fail");
            }
          })
          .catch(() => setStatus("fail"));
          
      } catch (error) {
        console.error('신청서 확인 실패:', error);
        setStatus("fail");
      }
    };

    checkApplicationAndProcess();
  }, [application_id, location.search, navigate]);

  // "홈으로 가기" 버튼 핸들러
  const handleGoHome = () => {
    navigate("/");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f5f7fa"
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, sm: 5 },
          borderRadius: 3,
          minWidth: 340,
          maxWidth: 420,
          textAlign: "center",
        }}
      >
        {status === "loading" && (
          <>
            <CircularProgress color="primary" size={44} sx={{ mb: 2 }} />
            <Typography variant="h6" fontWeight={700}>
              입금 요청을 처리하고 있습니다...
            </Typography>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 56, mb: 2 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>
              입금 요청이 완료되었습니다!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              담당자가 확인 후, 신청하신 계좌로 영업일 기준 2~3일 이내 입금 처리됩니다.
            </Typography>
            <Typography variant="body2" color="text.disabled">
              문의가 필요하시면 고객센터로 연락해 주세요.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<HomeIcon />}
                size="large"
                onClick={handleGoHome}
                sx={{
                  px: 4,
                  borderRadius: 2,
                  fontWeight: 700,
                  boxShadow: 2,
                }}
              >
                홈으로 가기
              </Button>
            </Stack>
          </>
        )}

        {status === "alreadyAccepted" && (
          <>
            <WarningAmberOutlinedIcon color="warning" sx={{ fontSize: 56, mb: 2 }} />
            <Typography variant="h6" fontWeight={700} color="warning.main" gutterBottom>
              이미 입금 요청이 처리된 신청입니다
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              이 신청건은 이미 입금 요청 또는 처리가 진행된 상태입니다.<br />
              추가 문의가 필요하시면 고객센터로 연락해 주세요.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<HomeIcon />}
                size="large"
                onClick={handleGoHome}
                sx={{
                  px: 4,
                  borderRadius: 2,
                  fontWeight: 700,
                  boxShadow: 2,
                }}
              >
                홈으로 가기
              </Button>
            </Stack>
          </>
        )}

        {status === "fail" && (
          <>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 56, mb: 2 }} />
            <Typography variant="h6" fontWeight={700} color="error" gutterBottom>
              입금 요청 처리에 실패했습니다
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              시스템 오류이거나 신청서 정보를 찾을 수 없습니다.
              <br />
              계속 문제가 발생할 경우 고객센터로 문의해 주세요.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={() => window.location.reload()}
                sx={{
                  px: 4,
                  borderRadius: 2,
                  fontWeight: 700,
                }}
              >
                다시 시도
              </Button>
              <Button
                variant="contained"
                startIcon={<HomeIcon />}
                size="large"
                onClick={handleGoHome}
                sx={{
                  px: 4,
                  borderRadius: 2,
                  fontWeight: 700,
                  boxShadow: 2,
                }}
              >
                홈으로 가기
              </Button>
            </Stack>
          </>
        )}
      </Paper>
    </Box>
  );
}
